import React, { useEffect, useState } from "react";
import { hasRole, isAdminLevel } from "../../lib/roles";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import { Plus, X, Camera, Clock } from "lucide-react";
import { useAuth } from "../../lib/auth";
import ImageUploader from "../../components/ImageUploader";
import DateInput from "../../components/DateInput";
import Paginated from "../../components/Paginated";
import { assistGet } from "../../lib/assist";

const SEQ = {
  design: ["received", "designing", "welding", "polishing", "finished", "delivered"],
  redesign: ["received", "designing", "welding", "polishing", "redesigning", "finished", "delivered"],
};
// "2.5" grams → "2g 500mg" — milligram-precise metal display
const fmtMetal = (g) => {
  const total = Number(g) || 0;
  const grams = Math.floor(total);
  const mg = Math.round((total - grams) * 1000);
  if (grams === 0 && mg === 0) return "0g";
  return `${grams}g${mg ? ` ${mg}mg` : ""}`;
};

const MIN_GAPS = { designing: 1, welding: 5, polishing: 10, redesigning: 7, finished: 15, delivered: 5 };
const CATEGORIES = ["Rings", "Necklaces", "Earrings", "Bangles", "Chains", "Pendants", "Bracelets", "Mangalsutra", "Nose Pins", "Anklets"];

const nextStep = (job) => {
  const seq = SEQ[job.job_type || "design"];
  const i = seq.indexOf(job.status);
  return i >= 0 && i < seq.length - 1 ? seq[i + 1] : null;
};
const minutesSinceLast = (job) => {
  const hist = job.status_history || [];
  if (!hist.length) return Infinity;
  return (Date.now() - new Date(hist[hist.length - 1].at).getTime()) / 60000;
};

export default function KarigarAdmin() {
  const { user } = useAuth();
  const isKarigar = hasRole(user, "karigar");
  const canAssign = hasRole(user, ["super_admin", "major_admin", "admin", "salesperson"]);
  // ONLY the karigar (on his own jobs) and the Super Admin can update status.
  // Salesperson / admin / major admin assign jobs and just WATCH the updates.
  const canUpdateStatus = isKarigar || hasRole(user, "super_admin");
  const canSetRates = hasRole(user, ["super_admin", "major_admin"]);
  const canPayout = hasRole(user, ["super_admin", "major_admin", "admin"]);

  const [jobs, setJobs] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [rates, setRates] = useState([]);
  const [tab, setTab] = useState(hasRole(user, "karigar") ? "myjobs" : "board");
  const [showForm, setShowForm] = useState(false);
  const [deliverJob, setDeliverJob] = useState(null);
  const [compJob, setCompJob] = useState(null); // admin: change a job's compensation (money ↔ metal)
  const [tick, setTick] = useState(0); // re-render every 20s so gap timers update

  const assist = assistGet();
  const [logQ, setLogQ] = useState("");
  const emptyJob = { customer_phone: assist?.phone || "", customer_name: assist?.name || "", karigar_id: "", job_type: "design", metal: "Gold", purity: "22K", grams: 0, category: "Rings", design_notes: "", design_images: [], raw_images: [], stone_required: false, stone_details: "", stone_images: [], advance_amount: 0, expected_date: "", compensation_type: "amount", comp_metal: "Gold", comp_purity: "22K", comp_grams: 0, comp_milligrams: 0 };
  const [form, setForm] = useState(emptyJob);

  const load = () => api.get("/api/crm/karigar/jobs").then((r) => setJobs(r.data || []));
  useEffect(() => {
    load();
    api.get("/api/crm/karigar/rates").then((r) => setRates(r.data || [])).catch(() => {});
    if (canAssign) api.get("/api/crm/users?role=karigar").then((r) => setKarigars(r.data || [])).catch(() => {});
    const t = setInterval(() => setTick((x) => x + 1), 20000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  // Auto rate preview while filling the assign form
  const matchedRate = rates.find((r) => r.metal === form.metal && r.category === form.category && r.job_type === form.job_type)
    || rates.find((r) => r.metal === form.metal && r.category === form.category);
  const autoAmount = matchedRate ? matchedRate.rate_per_gram * (+form.grams || 0) : 0;

  const submitJob = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.customer_phone)) { toast.error("Enter the customer's valid 10-digit phone"); return; }
    if (!form.raw_images.length) { toast.error("Take a photo of the raw gold / old piece while assigning"); return; }
    if (form.stone_required && !form.stone_images.length) { toast.error("Take a photo of the customer-provided stones"); return; }
    try {
      await api.post("/api/crm/karigar/jobs", { ...form, grams: +form.grams, advance_amount: +form.advance_amount, comp_grams: +form.comp_grams || 0, comp_milligrams: +form.comp_milligrams || 0 });
      toast.success("Karigar job assigned");
      setShowForm(false); setForm(emptyJob); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (job, extra = {}) => {
    const status = nextStep(job);
    if (!status) return;
    if (status === "delivered") { setDeliverJob(job); return; }
    try {
      await api.patch(`/api/crm/karigar/jobs/${job.id}/status`, { status, note: extra.note || "", photos: extra.photos || [] });
      toast.success(`Updated to ${status}`);
      load();
      return true;
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); return false; }
  };

  const cols = ["received", "designing", "welding", "polishing", "redesigning", "finished", "delivered"];

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><div className="eyebrow">Workshop</div><h1 className="font-display text-4xl text-maroon">Karigar Workflow</h1>
          {isKarigar && <p className="text-xs text-ash mt-1">Jobs are assigned to you by the showroom — you cannot assign a job to yourself.</p>}
        </div>
        <div className="flex gap-2">
          <div className="flex border border-line bg-white">
            {[...(isKarigar ? [["myjobs", "My Jobs"]] : []), ["board", "Job Board"], ["logs", "Activity Logs"], ["earnings", "Earnings & Payouts"], ...(canSetRates ? [["rates", "Rate Card"]] : [])].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-xs ${tab === k ? "bg-maroon text-pearl" : ""}`} data-testid={`karigar-tab-${k}`}>{l}</button>
            ))}
          </div>
          {canAssign && <button onClick={() => setShowForm(true)} className="btn-maroon" data-testid="add-karigar-job"><Plus size={14}/> Assign Job</button>}
        </div>
      </div>

      {/* ============ MY JOBS — full-page detailed cards (karigar) ============ */}
      {tab === "myjobs" && isKarigar && (
        <Paginated items={jobs} pageSize={1}>
          {(pageJobs) => pageJobs.map((j) => (
            <BigJobCard key={j.id} job={j} onUpdate={updateStatus} onDeliver={() => setDeliverJob(j)} tick={tick}/>
          ))}
        </Paginated>
      )}
      {tab === "myjobs" && isKarigar && jobs.length === 0 && (
        <div className="bg-white border border-line p-16 text-center text-ash">No jobs assigned to you yet.</div>
      )}

      {/* ============ BOARD ============ */}
      {tab === "board" && !canUpdateStatus && (
        <div className="bg-gold/10 border border-gold/40 p-3 text-xs mb-3" data-testid="karigar-viewonly-note">
          You can see the jobs <b>you assigned</b> and follow every update the karigar gives — but only the <b>karigar</b> and the <b>Super Admin</b> can change the job status.
        </div>
      )}
      {tab === "board" && (
        <div className="grid md:grid-cols-3 xl:grid-cols-7 gap-3 overflow-x-auto">
          {cols.map((st) => (
            <div key={st} className="bg-white border border-line min-h-[200px]">
              <div className="p-2 border-b border-line eyebrow text-[9px] text-center capitalize">{st}</div>
              <div className="p-2 space-y-2">
                {jobs.filter((j) => j.status === st && (st !== "redesigning" || j.job_type === "redesign")).map((j) => {
                  const nxt = nextStep(j);
                  const gap = nxt ? (MIN_GAPS[nxt] || 0) : 0;
                  const since = minutesSinceLast(j);
                  const locked = since < gap;
                  return (
                    <div key={j.id} className="border border-line p-2 text-xs bg-pearl/50" data-testid={`kjob-${j.id}`}>
                      <div className="flex items-center gap-2">
                        {(j.raw_images || j.design_images || [])[0] && <img src={(j.raw_images?.[0] || j.design_images?.[0])} alt="" className="w-8 h-8 object-cover border border-line"/>}
                        <div>
                          <div className="font-medium">{j.customer_name}</div>
                          <div className="text-[9px] text-ash">{j.category} · {j.grams}g {j.metal} {j.purity}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className={`px-1 text-[8px] uppercase tracking-widest ${j.job_type === "redesign" ? "bg-maroon/10 text-maroon" : "bg-gold/20"}`}>{j.job_type}</span>
                        <span className="text-[9px] text-ash">{j.karigar_name}</span>
                      </div>
                      <div className="text-[9px] text-ash mt-0.5 flex items-center gap-1 flex-wrap">
                        {j.compensation_type === "metal"
                          ? <>Pay: <b className="text-maroon">{fmtMetal(j.comp_weight_grams)} {j.comp_purity} {j.comp_metal}</b></>
                          : j.amount > 0 && <>Karigar amount: {fmtINR(j.amount)}</>}
                        {hasRole(user, ["super_admin", "major_admin", "admin"]) && j.status !== "delivered" && (
                          <button onClick={() => setCompJob(j)} className="text-maroon underline text-[9px]" data-testid={`change-pay-${j.id}`}>Change pay</button>
                        )}
                      </div>
                      {nxt && canUpdateStatus && (
                        <button
                          onClick={() => updateStatus(j)}
                          disabled={locked}
                          title={locked ? `Minimum ${gap} min gap — available in ${Math.ceil(gap - since)} min` : ""}
                          className="btn-outline text-[9px] mt-2 w-full justify-center disabled:opacity-50"
                          data-testid={`update-${j.id}`}
                        >
                          {locked ? <><Clock size={10}/> {nxt} in {Math.ceil(gap - since)}m</> : <>Update → {nxt}</>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============ ACTIVITY LOGS ============ */}
      {tab === "logs" && (
        <>
        <input placeholder="Search logs — customer / karigar / status…" className="border border-line px-3 py-2 bg-white text-sm w-full max-w-sm mb-3" value={logQ} onChange={(e) => setLogQ(e.target.value)} data-testid="karigar-log-search"/>
        <Paginated items={jobs.flatMap((j) => (j.status_history || []).map((h) => ({ ...h, job: j }))).filter((h) => !logQ || [h.job.customer_name, h.job.karigar_name, h.status, h.by, h.note].join(" ").toLowerCase().includes(logQ.toLowerCase())).sort((a, b) => new Date(b.at) - new Date(a.at))} pageSize={15}>
          {(rows) => (
            <div className="bg-white border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-pearl border-b border-line">
                  <tr>{["When", "Customer", "Karigar", "Item", "Status", "Note", "Updated By"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((h, i) => (
                    <tr key={`${h.job.id}-${i}`} className="border-b border-line/60">
                      <td className="p-3 text-xs">{new Date(h.at).toLocaleString()}</td>
                      <td className="p-3">{h.job.customer_name}</td>
                      <td className="p-3 text-xs">{h.job.karigar_name}</td>
                      <td className="p-3 text-xs text-ash">{h.job.category} · {h.job.grams}g {h.job.metal}</td>
                      <td className="p-3"><span className="bg-gold/20 text-maroon px-2 py-0.5 text-[10px] uppercase tracking-widest">{h.status}</span></td>
                      <td className="p-3 text-xs text-ash">{h.note || "—"}</td>
                      <td className="p-3 text-xs">{h.by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Paginated>
        </>
      )}

      {/* ============ EARNINGS & PAYOUTS ============ */}
      {tab === "earnings" && <EarningsTab isKarigar={isKarigar && !isAdminLevel(user)} canPayout={canPayout} karigars={karigars} me={user}/>}

      {/* ============ RATE CARD ============ */}
      {tab === "rates" && canSetRates && <RateCardTab rates={rates} reload={() => api.get("/api/crm/karigar/rates").then((r) => setRates(r.data || []))}/>}

      {/* ============ ASSIGN JOB MODAL ============ */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={submitJob} className="bg-white border border-line p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">Assign Karigar Job</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>

            {/* Scenario toggle: design (new from raw gold) vs redesign (old piece → new) */}
            <div className="flex border border-line overflow-hidden">
              <button type="button" onClick={() => setForm({...form, job_type: "design"})} className={`flex-1 py-2 text-sm ${form.job_type === "design" ? "bg-maroon text-pearl" : ""}`} data-testid="jobtype-design">New Design (raw gold)</button>
              <button type="button" onClick={() => setForm({...form, job_type: "redesign"})} className={`flex-1 py-2 text-sm border-l border-line ${form.job_type === "redesign" ? "bg-maroon text-pearl" : ""}`} data-testid="jobtype-redesign">Redesign (e.g. necklace → earrings)</button>
            </div>
            <p className="text-[10px] text-ash">{form.job_type === "design" ? "6 steps: received → designing → welding → polishing → finished → delivered" : "7 steps: received → designing → welding → polishing → redesigning → finished → delivered"}</p>

            <div className="grid grid-cols-2 gap-3">
              <input required placeholder="Customer name" className="border border-line px-3 py-2 bg-pearl" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} data-testid="kj-cust-name"/>
              <input required placeholder="Customer phone (10 digits)" maxLength={10} className="border border-line px-3 py-2 bg-pearl" value={form.customer_phone} onChange={(e) => setForm({...form, customer_phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="kj-cust-phone"/>
            </div>

            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Select karigar (specialization shown)</span>
              <select required className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.karigar_id} onChange={(e) => setForm({...form, karigar_id: e.target.value})} data-testid="kj-karigar">
                <option value="">Choose…</option>
                {karigars.map((k) => <option key={k.id} value={k.id}>{k.name}{k.specialization ? ` — ${k.specialization}` : ""}</option>)}
              </select>
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <select className="border border-line px-2 py-2 bg-pearl" value={form.metal} onChange={(e) => setForm({...form, metal: e.target.value})} data-testid="kj-metal"><option>Gold</option><option>Silver</option></select>
              <select className="border border-line px-2 py-2 bg-pearl" value={form.purity} onChange={(e) => setForm({...form, purity: e.target.value})}>
                {["24K","23K","22K","21K","20K","18K","14K","10K","9K","Silver"].map((p) => <option key={p}>{p}</option>)}
              </select>
              <input required type="number" step="0.01" placeholder="Grams" className="border border-line px-2 py-2 bg-pearl" value={form.grams || ""} onChange={(e) => setForm({...form, grams: e.target.value})} data-testid="kj-grams"/>
              <select className="border border-line px-2 py-2 bg-pearl" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="kj-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>

            {/* Compensation — money (auto from rate card) OR metal (gold/silver) */}
            <div className="border border-line p-3 space-y-2" data-testid="kj-compensation">
              <div className="eyebrow text-[10px]">How will this karigar be paid for this job?</div>
              <div className="flex border border-line overflow-hidden w-fit">
                <button type="button" onClick={() => setForm({...form, compensation_type: "amount"})}
                  className={`px-4 py-2 text-xs ${form.compensation_type !== "metal" ? "bg-maroon text-pearl" : "bg-white"}`}
                  data-testid="kj-comp-amount">₹ Amount (auto)</button>
                <button type="button" onClick={() => setForm({...form, compensation_type: "metal"})}
                  className={`px-4 py-2 text-xs border-l border-line ${form.compensation_type === "metal" ? "bg-maroon text-pearl" : "bg-white"}`}
                  data-testid="kj-comp-metal">Metal (gold / silver)</button>
              </div>

              {form.compensation_type !== "metal" ? (
                <div className={`text-xs p-2 border ${matchedRate ? "border-green-600 bg-green-50 text-green-800" : "border-line bg-pearl text-ash"}`} data-testid="kj-auto-amount">
                  {matchedRate
                    ? `Karigar amount (auto): ${fmtINR(matchedRate.rate_per_gram)}/g × ${form.grams || 0}g = ${fmtINR(autoAmount)} (${form.metal} · ${form.category} · ${form.job_type})`
                    : `No rate set yet for ${form.metal} · ${form.category} · ${form.job_type} — Super/Major Admin can add it in the Rate Card tab.`}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                    <label className="text-sm"><span className="eyebrow text-[10px]">Metal given</span>
                      <select className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={form.comp_metal} onChange={(e) => setForm({...form, comp_metal: e.target.value})} data-testid="kj-comp-metal-sel">
                        <option>Gold</option><option>Silver</option>
                      </select>
                    </label>
                    <label className="text-sm"><span className="eyebrow text-[10px]">Carat</span>
                      <select className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={form.comp_purity} onChange={(e) => setForm({...form, comp_purity: e.target.value})} data-testid="kj-comp-purity">
                        {["24K","23K","22K","21K","20K","18K","14K","10K","9K"].map((k) => <option key={k}>{k}</option>)}
                      </select>
                    </label>
                    <label className="text-sm"><span className="eyebrow text-[10px]">Grams</span>
                      <input type="number" min="0" className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={form.comp_grams || ""} onChange={(e) => setForm({...form, comp_grams: e.target.value})} data-testid="kj-comp-grams"/>
                    </label>
                    <label className="text-sm"><span className="eyebrow text-[10px]">Milligrams</span>
                      <input type="number" min="0" max="999" className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={form.comp_milligrams || ""} onChange={(e) => setForm({...form, comp_milligrams: e.target.value})} data-testid="kj-comp-mg"/>
                    </label>
                  </div>
                  <div className="text-xs p-2 border border-gold bg-gold/10 text-ink" data-testid="kj-comp-preview">
                    Instead of money, this karigar will receive <b>{+form.comp_grams || 0}g {+form.comp_milligrams ? `${+form.comp_milligrams}mg` : ""} of {form.comp_purity} {form.comp_metal}</b> for this job.
                  </div>
                </>
              )}
            </div>

            <textarea placeholder="Design notes — what type of design needs to be made" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={form.design_notes} onChange={(e) => setForm({...form, design_notes: e.target.value})} data-testid="kj-notes"/>

            <div>
              <div className="text-xs font-medium mb-1"><Camera size={12} className="inline"/> Photos of the raw gold / old piece <span className="text-red-600">*</span></div>
              <ImageUploader value={form.raw_images} onChange={(v) => setForm({...form, raw_images: v})} kind="karigar" maxFiles={4}/>
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Reference images of the design to be made</div>
              <ImageUploader value={form.design_images} onChange={(v) => setForm({...form, design_images: v})} kind="karigar" maxFiles={4}/>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.stone_required} onChange={(e) => setForm({...form, stone_required: e.target.checked})} data-testid="kj-stone-toggle"/>
              Stones need to be fitted
            </label>
            {form.stone_required && (
              <>
                <input placeholder="Stone details (type, count, size…)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.stone_details} onChange={(e) => setForm({...form, stone_details: e.target.value})} data-testid="kj-stone-details"/>
                <div>
                  <div className="text-xs font-medium mb-1">Photos of the customer-provided stones <span className="text-red-600">*</span></div>
                  <ImageUploader value={form.stone_images} onChange={(v) => setForm({...form, stone_images: v})} kind="karigar" maxFiles={3}/>
                </div>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm"><span className="eyebrow text-[10px]">Advance (₹)</span>
                <input type="number" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.advance_amount || ""} onChange={(e) => setForm({...form, advance_amount: e.target.value})}/>
              </label>
              <label className="text-sm"><span className="eyebrow text-[10px]">Expected date</span>
                <DateInput className="mt-1" yearsBack={0} yearsAhead={2} value={form.expected_date} onChange={(v) => setForm({...form, expected_date: v})} data-testid="kj-expected"/>
              </label>
            </div>
            <button className="btn-maroon w-full justify-center" data-testid="kj-submit">Assign Job</button>
          </form>
        </div>
      )}

      {/* ============ DELIVER MODAL ============ */}
      {deliverJob && <DeliverModal job={deliverJob} close={() => setDeliverJob(null)} done={() => { setDeliverJob(null); load(); }}/>}
      {compJob && <CompensationModal job={compJob} close={() => setCompJob(null)} done={() => { setCompJob(null); load(); }}/>}
    </div>
  );
}

/* ---- Delivered step: hand-over person, delivery photo, final weight ---- */
/* Full-page detailed job card — every detail and image large, update controls below */
function BigJobCard({ job: j, onUpdate, onDeliver, tick }) {
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const nxt = nextStep(j);
  const gap = nxt ? (MIN_GAPS[nxt] || 0) : 0;
  const since = minutesSinceLast(j);
  const locked = since < gap;
  const seq = j.job_type === "redesign"
    ? ["received", "designing", "welding", "polishing", "redesigning", "finished", "delivered"]
    : ["received", "designing", "welding", "polishing", "finished", "delivered"];
  const curIdx = seq.indexOf(j.status);

  const ImgGroup = ({ title, urls }) => (urls || []).length > 0 && (
    <div>
      <div className="eyebrow text-[10px] mb-2">{title}</div>
      <div className="flex flex-wrap gap-3">
        {urls.map((u, i) => (
          <a key={i} href={u} target="_blank" rel="noreferrer">
            <img src={u} alt="" className="h-40 w-40 object-cover border border-line hover:border-maroon"/>
          </a>
        ))}
      </div>
    </div>
  );

  const doUpdate = async () => {
    setBusy(true);
    const ok = await onUpdate(j, { note, photos });
    if (ok) { setNote(""); setPhotos([]); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-line p-6 lg:p-8 space-y-6 min-h-[70vh]" data-testid={`bigjob-${j.id}`}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="eyebrow text-[10px]">Job {j.id.slice(0, 8).toUpperCase()} · {new Date(j.created_at).toLocaleString()}</div>
          <div className="font-display text-3xl text-maroon">{j.customer_name}</div>
          <div className="text-sm text-ash mt-1">{j.category} · {j.grams}g {j.metal} {j.purity} · assigned by {j.created_by_name} ({(j.created_by_role || "").replace("_", " ")})</div>
        </div>
        <div className="text-right">
          <span className={`px-3 py-1 text-xs uppercase tracking-widest ${j.job_type === "redesign" ? "bg-maroon/10 text-maroon" : "bg-gold/20 text-ink"}`}>{j.job_type}</span>
          <div className="font-display text-2xl text-maroon mt-2">{j.status}</div>
          {j.compensation_type === "metal" ? (
            <div className="text-xs text-ash mt-1">You receive for this job: <b className="text-maroon">{j.comp_grams || 0}g{j.comp_milligrams ? ` ${j.comp_milligrams}mg` : ""} {j.comp_purity} {j.comp_metal}</b></div>
          ) : j.amount > 0 && <div className="text-xs text-ash mt-1">Your amount for this job: <b className="text-maroon">{fmtINR(j.amount)}</b></div>}
        </div>
      </div>

      {/* Step progress */}
      <div>
        <div className="flex items-center">
          {seq.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] shrink-0 ${i <= curIdx ? "bg-maroon text-pearl" : "bg-line text-ash"}`}>{i + 1}</div>
              {i < seq.length - 1 && <div className={`h-1 flex-1 ${i < curIdx ? "bg-maroon" : "bg-line"}`}/>}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between text-[9px] uppercase tracking-wider text-ash mt-1">
          {seq.map((s) => <span key={s}>{s}</span>)}
        </div>
      </div>

      {/* All details */}
      <div className="grid lg:grid-cols-2 gap-x-8 gap-y-2 text-sm">
        <Detail l="Customer phone" v={j.customer_phone}/>
        <Detail l="Expected date" v={j.expected_date ? new Date(j.expected_date).toLocaleDateString("en-GB") : "—"}/>
        <Detail l="Advance received" v={j.advance_amount ? fmtINR(j.advance_amount) : "—"}/>
        <Detail l="Stone to be fitted" v={j.stone_required ? (j.stone_details || "Yes") : "No"}/>
        <Detail l="Design notes" v={j.design_notes || "—"} wide/>
        {j.making_loss_grams != null && <Detail l="Making loss" v={`${j.making_loss_grams}g`}/>}
        {j.final_weight_grams != null && <Detail l="Final weight after work" v={`${j.final_weight_grams}g`}/>}
        {j.handed_to && <Detail l="Delivered to" v={j.handed_to}/>}
      </div>

      {/* All images — large */}
      <div className="space-y-5">
        <ImgGroup title="Raw gold / silver given" urls={j.raw_images}/>
        <ImgGroup title="Design to be made" urls={j.design_images}/>
        <ImgGroup title="Customer's stones" urls={j.stone_images}/>
        <ImgGroup title="Latest step photos" urls={j.last_photos}/>
        <ImgGroup title="Delivery photos" urls={j.delivery_photos}/>
      </div>

      {/* History with per-step notes/photos */}
      {(j.status_history || []).length > 0 && (
        <div>
          <div className="eyebrow text-[10px] mb-2">Step History</div>
          <div className="space-y-1">
            {(j.status_history || []).map((h, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-xs border-b border-line/50 py-1">
                <span className="bg-gold/20 px-1.5 py-0.5 uppercase tracking-widest text-[9px]">{h.status}</span>
                <span className="text-ash">{new Date(h.at).toLocaleString()}</span>
                <span>by {h.by}</span>
                {h.note && <span className="text-ash italic">— {h.note}</span>}
                {(h.photos || []).map((u, pi) => <a key={pi} href={u} target="_blank" rel="noreferrer"><img src={u} alt="" className="h-8 w-8 object-cover border border-line"/></a>)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Update controls — attach photos / note at ANY step, buttons in a row */}
      {nxt && (
        <div className="border-t-2 border-gold pt-4 space-y-3">
          <div className="eyebrow text-[10px]">Update this job — attach photos or a note for this step (optional)</div>
          <textarea rows={2} placeholder={`Note for the '${nxt}' step…`} className="w-full border border-line px-3 py-2 bg-pearl text-sm" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`bigjob-note-${j.id}`}/>
          <ImageUploader value={photos} onChange={setPhotos} kind="karigar" maxFiles={4}/>
          <div className="flex flex-wrap gap-3">
            {nxt !== "delivered" ? (
              <button onClick={doUpdate} disabled={locked || busy}
                className="btn-maroon disabled:opacity-50" data-testid={`bigjob-update-${j.id}`}>
                {locked ? <><Clock size={14}/> '{nxt}' available in {Math.ceil(gap - since)} min</> : <>Mark as {nxt}</>}
              </button>
            ) : (
              <button onClick={onDeliver} disabled={locked}
                className="btn-gold disabled:opacity-50" data-testid={`bigjob-deliver-${j.id}`}>
                {locked ? <><Clock size={14}/> Delivery available in {Math.ceil(gap - since)} min</> : <><Camera size={14}/> Deliver (photo + final weight + receiver)</>}
              </button>
            )}
            {locked && <span className="text-xs text-ash self-center">Minimum {gap} minutes required between '{j.status}' and '{nxt}'.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ l, v, wide }) => (
  <div className={wide ? "lg:col-span-2" : ""}>
    <span className="eyebrow text-[9px] block">{l}</span>
    <span>{v}</span>
  </div>
);

/* Admins can change how a job is compensated: money (auto) ↔ metal */
function CompensationModal({ job, close, done }) {
  const [f, setF] = useState({
    compensation_type: job.compensation_type === "metal" ? "metal" : "amount",
    comp_metal: job.comp_metal || "Gold",
    comp_purity: job.comp_purity || "22K",
    comp_grams: job.comp_grams || 0,
    comp_milligrams: job.comp_milligrams || 0,
  });
  const save = async () => {
    try {
      await api.patch(`/api/crm/karigar/jobs/${job.id}/compensation`, { ...f, comp_grams: +f.comp_grams || 0, comp_milligrams: +f.comp_milligrams || 0 });
      toast.success("Compensation updated");
      done();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
      <div className="bg-white border border-line p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl text-maroon">Change pay — {job.customer_name}</div>
          <button onClick={close}><X size={18}/></button>
        </div>
        <div className="text-xs text-ash">{job.category} · {job.grams}g {job.metal} · karigar {job.karigar_name}</div>
        <div className="flex border border-line overflow-hidden w-fit">
          <button type="button" onClick={() => setF({...f, compensation_type: "amount"})} className={`px-4 py-2 text-xs ${f.compensation_type !== "metal" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="comp-modal-amount">₹ Amount (auto)</button>
          <button type="button" onClick={() => setF({...f, compensation_type: "metal"})} className={`px-4 py-2 text-xs border-l border-line ${f.compensation_type === "metal" ? "bg-maroon text-pearl" : "bg-white"}`} data-testid="comp-modal-metal">Metal</button>
        </div>
        {f.compensation_type === "metal" ? (
          <div className="grid grid-cols-2 gap-2">
            <label className="text-sm"><span className="eyebrow text-[10px]">Metal</span>
              <select className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={f.comp_metal} onChange={(e) => setF({...f, comp_metal: e.target.value})}><option>Gold</option><option>Silver</option></select>
            </label>
            <label className="text-sm"><span className="eyebrow text-[10px]">Carat</span>
              <select className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={f.comp_purity} onChange={(e) => setF({...f, comp_purity: e.target.value})}>
                {["24K","23K","22K","21K","20K","18K","14K","10K","9K"].map((k) => <option key={k}>{k}</option>)}
              </select>
            </label>
            <label className="text-sm"><span className="eyebrow text-[10px]">Grams</span>
              <input type="number" min="0" className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={f.comp_grams || ""} onChange={(e) => setF({...f, comp_grams: e.target.value})} data-testid="comp-modal-grams"/>
            </label>
            <label className="text-sm"><span className="eyebrow text-[10px]">Milligrams</span>
              <input type="number" min="0" max="999" className="w-full border border-line px-2 py-2 bg-pearl mt-1" value={f.comp_milligrams || ""} onChange={(e) => setF({...f, comp_milligrams: e.target.value})} data-testid="comp-modal-mg"/>
            </label>
          </div>
        ) : (
          <div className="text-xs text-ash p-2 border border-line bg-pearl">Switching back to money — the amount is recalculated automatically from the rate card ({fmtINR(job.rate_per_gram || 0)}/g × {job.grams}g).</div>
        )}
        <button onClick={save} className="btn-maroon w-full justify-center" data-testid="comp-modal-save">Save</button>
      </div>
    </div>
  );
}

function DeliverModal({ job, close, done }) {
  const [handedTo, setHandedTo] = useState("");
  const [photos, setPhotos] = useState([]);
  const [weight, setWeight] = useState(job.grams || "");
  const [note, setNote] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/api/crm/karigar/jobs/${job.id}/status`, {
        status: "delivered", note, photos, handed_to: handedTo, final_weight_grams: +weight,
      });
      toast.success("Marked delivered");
      done();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
      <form onSubmit={submit} className="bg-white border border-line p-6 w-full max-w-md space-y-3 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="font-display text-2xl text-maroon">Deliver — {job.customer_name}</div>
        <p className="text-xs text-ash">Given weight was {job.grams}g. Enter the final weight after karigar work (wastage removed, stones fitted if any), a delivery photo of the finished design, and to whom it is handed over.</p>
        <input required placeholder="Handed over to (salesperson / delivery person / admin name)" className="w-full border border-line px-3 py-2 bg-pearl" value={handedTo} onChange={(e) => setHandedTo(e.target.value)} data-testid="deliver-handedto"/>
        <label className="block text-sm"><span className="eyebrow text-[10px]">Final weight (grams)</span>
          <input required type="number" step="0.01" className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={weight} onChange={(e) => setWeight(e.target.value)} data-testid="deliver-weight"/>
        </label>
        <div>
          <div className="text-xs font-medium mb-1">Delivery photo of the finished design <span className="text-red-600">*</span></div>
          <ImageUploader value={photos} onChange={setPhotos} kind="karigar" maxFiles={3}/>
        </div>
        <textarea placeholder="Note (optional)" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={note} onChange={(e) => setNote(e.target.value)}/>
        <button className="btn-maroon w-full justify-center" data-testid="deliver-submit">Mark Delivered</button>
      </form>
    </div>
  );
}

/* ---- Earnings & monthly payouts ---- */
function EarningsTab({ isKarigar, canPayout, karigars, me }) {
  const [sel, setSel] = useState(isKarigar ? me.id : "");
  const [data, setData] = useState(null);
  const [payout, setPayout] = useState(null); // payout form state
  const load = (kid) => {
    if (!kid) { setData(null); return; }
    api.get(`/api/crm/karigar/earnings?karigar_id=${kid}`).then((r) => setData(r.data));
  };
  useEffect(() => { load(sel); /* eslint-disable-next-line */ }, [sel]);

  return (
    <div className="space-y-4">
      {!isKarigar && (
        <select className="border border-line px-3 py-2 bg-white text-sm" value={sel} onChange={(e) => setSel(e.target.value)} data-testid="earnings-karigar-select">
          <option value="">Select karigar…</option>
          {karigars.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
      )}
      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-line p-4 text-center"><div className="eyebrow text-[9px]">Total Earned (LTV)</div><div className="font-display text-2xl text-maroon">{fmtINR(data.total_earned)}</div></div>
            <div className="bg-white border border-line p-4 text-center"><div className="eyebrow text-[9px]">Paid by Swayambhu Gold</div><div className="font-display text-2xl text-maroon">{fmtINR(data.total_paid)}</div></div>
            <div className="bg-white border border-line p-4 text-center"><div className="eyebrow text-[9px]">Outstanding (month-end)</div><div className="font-display text-2xl text-maroon">{fmtINR(data.outstanding)}</div></div>
          </div>
          {/* Metal received INSTEAD of money — lifetime totals + per-job list */}
          <div className="bg-white border-2 border-gold p-4" data-testid="metal-received">
            <div className="eyebrow text-[10px] mb-2">Metal Received Instead of Payment (lifetime)</div>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="text-center border border-line p-3">
                <div className="font-display text-2xl text-maroon">{fmtMetal(data.gold_received_g)}</div>
                <div className="text-[9px] uppercase tracking-widest text-ash">Gold received</div>
              </div>
              <div className="text-center border border-line p-3">
                <div className="font-display text-2xl text-maroon">{fmtMetal(data.silver_received_g)}</div>
                <div className="text-[9px] uppercase tracking-widest text-ash">Silver received</div>
              </div>
            </div>
            {(data.metal_jobs || []).length > 0 && (
              <div className="mt-3 max-h-40 overflow-y-auto">
                {(data.metal_jobs || []).map((j) => (
                  <div key={j.id} className="text-xs border-b border-line/50 py-1 flex justify-between" data-testid={`metal-job-${j.id}`}>
                    <span>{j.category} · {j.delivered_at ? new Date(j.delivered_at).toLocaleDateString("en-GB") : "—"}</span>
                    <b className="text-maroon">{fmtMetal(j.comp_weight_grams)} {j.comp_purity} {j.comp_metal}</b>
                  </div>
                ))}
              </div>
            )}
            {(data.metal_jobs || []).length === 0 && <div className="text-xs text-ash mt-2">No jobs compensated in metal yet.</div>}
          </div>

          {canPayout && data.outstanding > 0 && (
            <button onClick={() => setPayout({ amount: data.outstanding, method: "UPI", reference: "", proof_images: [], note: "" })} className="btn-maroon text-xs" data-testid="karigar-payout-btn">Record Month-End Payout</button>
          )}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-line">
              <div className="p-3 border-b border-line eyebrow text-[10px]">Delivered Jobs (per-job amount)</div>
              <div className="max-h-72 overflow-y-auto">
                {(data.jobs || []).map((j) => (
                  <div key={j.id} className="p-2 border-b border-line/60 text-xs flex justify-between">
                    <span>{j.category} · {j.grams}g {j.metal} · {new Date(j.delivered_at || j.created_at).toLocaleDateString("en-GB")}</span>
                    <b className="text-maroon">{j.compensation_type === "metal" ? `${fmtMetal(j.comp_weight_grams)} ${j.comp_purity} ${j.comp_metal}` : fmtINR(j.amount || 0)}</b>
                  </div>
                ))}
                {(data.jobs || []).length === 0 && <div className="p-6 text-center text-ash text-xs">No delivered jobs yet.</div>}
              </div>
            </div>
            <div className="bg-white border border-line">
              <div className="p-3 border-b border-line eyebrow text-[10px]">Payout Logs (visible to karigar & admins)</div>
              <div className="max-h-72 overflow-y-auto">
                {(data.payouts || []).map((p) => (
                  <div key={p.id} className="p-2 border-b border-line/60 text-xs">
                    <div className="flex justify-between"><b className="text-maroon">{fmtINR(p.amount)}</b><span>{p.method}{p.reference ? ` · ${p.reference}` : ""}</span></div>
                    <div className="text-ash">{new Date(p.paid_at).toLocaleDateString("en-GB")} · by {p.paid_by}</div>
                    {(p.proof_images || []).length > 0 && <div className="flex gap-1 mt-1">{p.proof_images.map((u, i) => <img key={i} src={u} alt="proof" className="w-10 h-10 object-cover border border-line"/>)}</div>}
                  </div>
                ))}
                {(data.payouts || []).length === 0 && <div className="p-6 text-center text-ash text-xs">No payouts yet.</div>}
              </div>
            </div>
          </div>
        </>
      )}
      {payout && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPayout(null)}>
          <form onSubmit={async (e) => {
            e.preventDefault();
            try {
              await api.post("/api/crm/karigar/payouts", { ...payout, karigar_id: sel, amount: +payout.amount });
              toast.success("Payout recorded — outstanding reset");
              setPayout(null); load(sel);
            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
          }} className="bg-white border border-line p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl text-maroon">Karigar Payout</div>
            <input required type="number" step="0.01" placeholder="Amount (₹)" className="w-full border border-line px-3 py-2 bg-pearl" value={payout.amount} onChange={(e) => setPayout({...payout, amount: e.target.value})} data-testid="payout-amount"/>
            <select className="w-full border border-line px-3 py-2 bg-pearl" value={payout.method} onChange={(e) => setPayout({...payout, method: e.target.value})} data-testid="payout-method">
              {["UPI", "Bank Transfer", "Cheque", "Cash"].map((m) => <option key={m}>{m}</option>)}
            </select>
            <input placeholder={payout.method === "Cheque" ? "Cheque number & details (required)" : "Reference / transaction ID"} className="w-full border border-line px-3 py-2 bg-pearl" value={payout.reference} onChange={(e) => setPayout({...payout, reference: e.target.value})} data-testid="payout-ref"/>
            <div>
              <div className="text-xs font-medium mb-1">Proof ({payout.method === "UPI" ? "UPI screenshot" : payout.method === "Cheque" ? "cheque photo" : "optional"})</div>
              <ImageUploader value={payout.proof_images} onChange={(v) => setPayout({...payout, proof_images: v})} kind="payout" maxFiles={3}/>
            </div>
            <button className="btn-maroon w-full justify-center" data-testid="payout-submit">Record Payout</button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ---- Rate card (₹/gram) — Super & Major Admin ---- */
function RateCardTab({ rates, reload }) {
  const [form, setForm] = useState({ metal: "Gold", category: "Rings", job_type: "design", rate_per_gram: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/crm/karigar/rates", { ...form, rate_per_gram: +form.rate_per_gram });
      toast.success("Rate saved"); setForm({ ...form, rate_per_gram: "" }); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <p className="text-xs text-ash">₹/gram rate per metal, category and job type. This auto-calculates the karigar's amount when a job is assigned (e.g. Gold Rings design ₹300/g × 10g = ₹3,000).</p>
      <form onSubmit={save} className="bg-white border border-line p-4 flex flex-wrap gap-2 items-end">
        <select className="border border-line px-2 py-2 bg-pearl text-sm" value={form.metal} onChange={(e) => setForm({...form, metal: e.target.value})} data-testid="rate-metal"><option>Gold</option><option>Silver</option></select>
        <select className="border border-line px-2 py-2 bg-pearl text-sm" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="rate-category">
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select className="border border-line px-2 py-2 bg-pearl text-sm" value={form.job_type} onChange={(e) => setForm({...form, job_type: e.target.value})} data-testid="rate-jobtype"><option value="design">Design</option><option value="redesign">Redesign</option></select>
        <input required type="number" step="0.01" placeholder="₹ per gram" className="border border-line px-3 py-2 bg-pearl text-sm w-32" value={form.rate_per_gram} onChange={(e) => setForm({...form, rate_per_gram: e.target.value})} data-testid="rate-value"/>
        <button className="btn-maroon text-xs" data-testid="rate-save">Save Rate</button>
      </form>
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line"><tr>{["Metal", "Category", "Job Type", "₹ / gram", "Updated"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {rates.map((r) => (
              <tr key={`${r.metal}-${r.category}-${r.job_type}`} className="border-b border-line/60">
                <td className="p-3">{r.metal}</td><td className="p-3">{r.category}</td><td className="p-3 capitalize">{r.job_type}</td>
                <td className="p-3 text-maroon font-medium">{fmtINR(r.rate_per_gram)}</td>
                <td className="p-3 text-xs text-ash">{r.updated_by} · {r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-GB") : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rates.length === 0 && <div className="p-8 text-center text-ash text-sm">No rates set yet.</div>}
      </div>
    </div>
  );
}
